import { ALL_USERS_KEY, candidacyStatusToPipelineStage } from "@/lib/constants";
import { dashboard } from "@/lib/dashboard";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import {
  CandidacyStatus,
  DashboardSelectedUser,
  JobPipelineSortKey,
  JobPipelineSortState,
  MinimalCandidacy,
  PipelineFilterAndSortState,
  PipelineStage,
  QuoMessageDirection,
  SortOrder,
} from "@/types";

/**
 * Filters candidacies based on the provided filter state.
 * This is the single source of truth for pipeline filtering logic.
 */
export function filterCandidacies(
  candidacies: MinimalCandidacy[],
  filterState: PipelineFilterAndSortState,
  staffer: DashboardSelectedUser,
): MinimalCandidacy[] {
  const sourcesSet = new Set(filterState.sources);
  const locationsSet = new Set(filterState.locations);

  return candidacies.filter((candidacy) => {
    // 1. Hide rejected/closed unless includeRejected is on.
    const status = candidacy.lastCandidacyStatusChange.toStatus;
    if (
      (status === CandidacyStatus.REJECTED ||
        status === CandidacyStatus.CLOSED) &&
      !filterState.includeRejected
    ) {
      return false;
    }

    // 2. Hide could-not-contact unless includeCouldNotContact is on.
    if (
      status === CandidacyStatus.COULD_NOT_CONTACT &&
      !filterState.includeCouldNotContact
    ) {
      return false;
    }

    // 3. Hide employment-ended unless includeEmploymentEnded is on.
    if (
      status === CandidacyStatus.EMPLOYMENT_ENDED &&
      !filterState.includeEmploymentEnded
    ) {
      return false;
    }

    // 4. Filter by candidate name.
    if (filterState.name) {
      // If a candidate doesn't have a first or last name, include them
      if (candidacy.firstName && candidacy.lastName) {
        const fullName = formatting
          .fullName(candidacy.firstName, candidacy.lastName, "")
          .toLowerCase();
        if (!fullName.includes(filterState.name.toLowerCase())) {
          return false;
        }
      }
    }

    // 5. "Excellent score" / "Great score" chips — match per-bucket strictly.
    //    Excellent = 5 only, Great = 4 only. If both active, candidate must
    //    fall into at least one of the selected buckets.
    if (filterState.excellentScore || filterState.greatScore) {
      const latestAgenticScore = candidacy.agenticScores[0]?.toScore;
      const isExcellent = latestAgenticScore === 5;
      const isGreat = latestAgenticScore === 4;
      const matches =
        (filterState.excellentScore && isExcellent) ||
        (filterState.greatScore && isGreat);
      if (!matches) return false;
    }

    // 6. "Comms to send" — a comm is due: candidacy is in a comms stage, it's
    //    under the contact-attempt cap, and the next-action timer has elapsed.
    if (filterState.onlyCommsToSend) {
      if (
        !dashboard.isCommsToSend(
          status,
          candidacy.contactsAttempted,
          candidacy.nextActionAt,
          new Date(),
        )
      ) {
        return false;
      }
    }

    // 7. Source filter — narrow to candidacies whose source is selected.
    if (sourcesSet.size > 0) {
      if (!candidacy.source || !sourcesSet.has(candidacy.source)) {
        return false;
      }
    }

    // 8. Location filter
    if (locationsSet.size > 0) {
      if (!candidacy.location) return false;
      const stripped = locations.stripCountrySuffix(candidacy.location);
      if (!locationsSet.has(stripped)) return false;
    }

    // 9. Stevie Call Completed Filter
    if (filterState.stevieCallCompleted) {
      if (candidacy.voiceAiOutreach?.adminMetadata?.completed !== true) {
        return false;
      }
    }

    // 10. Staffer filter — unowned candidacies always pass; otherwise any owner match.
    if (staffer !== ALL_USERS_KEY) {
      if (candidacy.owners.length > 0) {
        if (!candidacy.owners.some((o) => o.userId === staffer)) {
          return false;
        }
      }
    }

    // 11. Commute cap — when set, hide candidacies whose commute exceeds the
    //     cap, plus any with no commute data.
    if (filterState.maxCommuteSeconds !== null) {
      const durationSeconds = candidacy.commuteInfo?.durationSeconds;
      if (
        durationSeconds === undefined ||
        durationSeconds > filterState.maxCommuteSeconds
      ) {
        return false;
      }
    }

    return true;
  });
}

// Per-key value extractors. `null` means "missing" — those rows sink to the
// end regardless of direction. Same pattern as the dashboard table sorter.
const getSortValue = (
  candidacy: MinimalCandidacy,
  key: JobPipelineSortState["key"],
  userNameMap: Record<number | string, string>,
  now: number,
): string | number | null => {
  switch (key) {
    case JobPipelineSortKey.Name:
      return (
        formatting
          .fullName(candidacy.firstName, candidacy.lastName, "")
          .toLowerCase() || null
      );
    case JobPipelineSortKey.Staffer: {
      if (candidacy.owners.length === 0) return null;
      return userNameMap[candidacy.owners[0].userId].toLowerCase();
    }
    case JobPipelineSortKey.TimeInStage:
      return (
        now - new Date(candidacy.lastCandidacyStatusChange.createdAt).getTime()
      );
    case JobPipelineSortKey.AgenticScore:
      return candidacy.agenticScores[0]?.toScore ?? null;
    case JobPipelineSortKey.LastPing:
      return candidacy.lastPing
        ? new Date(candidacy.lastPing.at).getTime()
        : null;
    case JobPipelineSortKey.Location:
      return candidacy.commuteInfo?.durationSeconds ?? null;
    default:
      return null;
  }
};

// The comm signal for the secondary sort: the newer of the last ping and a
// completed voice-AI call (which counts as incoming — the candidate picked up
// and engaged). Comms from before the candidacy existed don't count as contact.
const getLastCommSignal = (
  candidacy: MinimalCandidacy,
): { at: number; isIncoming: boolean } | null => {
  const createdAt = new Date(candidacy.createdAt).getTime();

  let signal: { at: number; isIncoming: boolean } | null = null;
  if (candidacy.lastPing) {
    const at = new Date(candidacy.lastPing.at).getTime();
    if (at > createdAt) {
      signal = {
        at,
        isIncoming:
          candidacy.lastPing.direction === QuoMessageDirection.INCOMING,
      };
    }
  }

  const voiceAiMetadata = candidacy.voiceAiOutreach?.adminMetadata;
  if (voiceAiMetadata?.completed && voiceAiMetadata.completedAt) {
    const at = new Date(voiceAiMetadata.completedAt).getTime();
    if (at > createdAt && (!signal || at > signal.at)) {
      signal = { at, isIncoming: true };
    }
  }

  return signal;
};

// Incoming-last floats above never-contacted, which floats above outgoing-last.
const COMM_RANK_INCOMING = 0;
const COMM_RANK_NEVER_CONTACTED = 1;
const COMM_RANK_OUTGOING = 2;

const commRank = (signal: { isIncoming: boolean } | null): number => {
  if (!signal) return COMM_RANK_NEVER_CONTACTED;
  return signal.isIncoming ? COMM_RANK_INCOMING : COMM_RANK_OUTGOING;
};

// Incoming: newest reply first. Outgoing: longest-waiting first.
const compareByLastComm = (
  a: MinimalCandidacy,
  b: MinimalCandidacy,
): number => {
  const aSignal = getLastCommSignal(a);
  const bSignal = getLastCommSignal(b);
  const rankDiff = commRank(aSignal) - commRank(bSignal);
  if (rankDiff !== 0) return rankDiff;
  if (!aSignal || !bSignal) return 0;
  return aSignal.isIncoming ? bSignal.at - aSignal.at : aSignal.at - bSignal.at;
};

// Applied candidacies rarely have comms yet, so newest application replaces
// the comm signal as their tiebreak.
const compareByCreatedAtDesc = (
  a: MinimalCandidacy,
  b: MinimalCandidacy,
): number => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const isApplied = (candidacy: MinimalCandidacy): boolean =>
  candidacyStatusToPipelineStage[
    candidacy.lastCandidacyStatusChange.toStatus
  ] === PipelineStage.APPLIED;

export function sortCandidacies(
  candidacies: MinimalCandidacy[],
  sortKeyAndOrder: JobPipelineSortState | null,
  userNameMap: Record<number | string, string>,
): MinimalCandidacy[] {
  // Null sort state = no column selected; fall back to timeInStage asc (freshest first).
  const effective = sortKeyAndOrder ?? {
    key: JobPipelineSortKey.TimeInStage,
    direction: SortOrder.Asc,
  };
  const directionMultiplier = effective.direction === SortOrder.Asc ? 1 : -1;
  const now = Date.now();
  return [...candidacies].sort((a, b) => {
    const aValue = getSortValue(a, effective.key, userNameMap, now);
    const bValue = getSortValue(b, effective.key, userNameMap, now);
    // Missing values sink to bottom regardless of direction; two missing
    // values are a tie and fall through to the secondary sort.
    if (aValue === null && bValue !== null) return 1;
    if (bValue === null && aValue !== null) return -1;
    if (aValue !== null && bValue !== null) {
      const primaryDiff =
        typeof aValue === "number" && typeof bValue === "number"
          ? (aValue - bValue) * directionMultiplier
          : String(aValue).localeCompare(String(bValue)) * directionMultiplier;
      if (primaryDiff !== 0) return primaryDiff;
    }

    // Tied on the selected column — the comm signal breaks the tie, except in
    // Applied, where recency does.
    if (isApplied(a) && isApplied(b)) return compareByCreatedAtDesc(a, b);
    return compareByLastComm(a, b);
  });
}
